import { Grid, Hidden, Theme, Typography, makeStyles } from '@material-ui/core';
import { useNetworkObserver } from 'data-layer-cr/dist/network';
import { NetworkResponse } from 'data-layer-cr/dist/network/NetworkTypes';
import {
  DefaultButton,
  DefaultCard,
  DropDown,
  FormHookResult,
  Modal,
  RadioButtonGroup,
  TextField,
  colors,
  useFormHook,
} from 'design-system';
import { useCallback, useEffect, useState } from 'react';
import { AnyAction } from 'redux';
import { payerNaturalPersonEquifax } from 'src/functions/equifax';
import { NaturalPersonEquifaxResponse } from 'src/models/responseTypes/NaturalPersonEquifaxResponse';
import PersonDataResponse from 'src/models/responseTypes/PersonValidationResponse';
import processRequest from 'src/services/processRequest';
import { REQUEST_SEARCH_VALIDATE_PERSON } from 'src/store/person/requestIdentifiers';
import PaymentIssuer from '../../../../../models/helperTypes/PaymentIssuer';

import makePaymentIssuerValidationSchema from './makePaymentIssuerModalValidationSchema';

import genders from '../../../../../constants/genders';
import { fullEntityList as idTypes } from '../../../../../constants/idTypes';

import PersonValidationAlert from '../../components/PersonValidationAlert';
import { LoadingEquifax } from '../../NaturalPersonData/components/LoadingEquifax';
import info from '../res/info.svg';

const useStyles = makeStyles((theme: Theme) => ({
  callToActionIcon: {
    width: 24,
  },
  callToActionCaption: {
    maxWidth: `calc(100% - 24px - ${theme.spacing(1)}px)`,
    marginLeft: theme.spacing(1),
  },
  mobileButtonContainerOffset: {
    height: 110 + theme.spacing(2),
    width: '100%',
  },
  mobileButtonContainer: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: colors.cloud,
    padding: 30,
  },
}));

/**
 * Extracted to prevent sonarcloud from complaining
 */
type MakeTextFieldProps = {
  key?: string;
  name: string;
  label: string;
  getInputProps: FormHookResult;
  onBlur?: () => void;
  isLoading?: boolean;
  helperText?: string;
};
function makeTextField({
  name,
  label,
  getInputProps,
  onBlur,
  isLoading,
  helperText,
}: MakeTextFieldProps): JSX.Element {
  return (
    <TextField
      inputName={name}
      inputLabel={label}
      defaultValue={getInputProps(name).value as string}
      onChange={getInputProps(name).onChange}
      customBlur={onBlur}
      inputError={getInputProps(name).error}
      inputErrorMessage={getInputProps(name).errorText}
      loading={isLoading}
      helperText={helperText}
    />
  );
}

function makeTextIdField({
  name,
  label,
  getInputProps,
  onBlur,
  isLoading,
  helperText,
}: MakeTextFieldProps): JSX.Element {
  return (
    <TextField
      inputName={name}
      inputLabel={label}
      defaultValue={getInputProps(name).value as string}
      onChange={(value) => getInputProps(name).onChange(value.toUpperCase())}
      customBlur={onBlur}
      inputError={getInputProps(name).error}
      inputErrorMessage={getInputProps(name).errorText}
      loading={isLoading}
      helperText={helperText}
    />
  );
}

const PaymentIssuerModal = ({
  editMode,
  currentValues,
  open,
  onCloseRequested,
  validatePersonAction,
  personValidateData,
  setNewPayerDataFieldsAction,
}: Props): JSX.Element => {
  const classes = useStyles();

  const [isLegalPayer, setIsLegalPayer] = useState(false);
  const [phoneText, setPhoneText] = useState('Celular');
  const requestingPerson = useNetworkObserver(REQUEST_SEARCH_VALIDATE_PERSON);
  const [refetch, setRefetch] = useState(false);
  const [showValidationAlert, setShowValidationAlert] = useState(false);
  const [errors, setErrors] = useState<{ error: boolean; errorText: string }>({
    error: false,
    errorText: '',
  });

  const { getInputProps, handleSubmit, updateMultipleInputs, isValid } =
    useFormHook<PaymentIssuer>(
      currentValues,
      (data): void => {
        if (
          isValid &&
          errors.error === false &&
          requestingPerson === 'success'
        ) {
          onCloseRequested(false, data);
        }
      },
      makePaymentIssuerValidationSchema(),
      { deferErrorCheck: true },
    );

  const [isLoading, setIsLoading] = useState(false);
  const identificationType = getInputProps('idType').value as string;

  function changeIdType(idtype: string): void {
    if (idtype === 'J') {
      setIsLegalPayer(true);
      setPhoneText('Número telefónico');
      getInputProps('lastNames').value = '';
      getInputProps('birthday').value = '';
      getInputProps('gender').value = '';
    } else {
      setIsLegalPayer(false);
      setPhoneText('Celular');
    }
  }

  /** The lint disable should prevent a render loop */
  useEffect(() => {
    if (open) {
      updateMultipleInputs(currentValues);

      const InitialIdType = getInputProps('idType').value?.toString();
      changeIdType(InitialIdType || 'N');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (requestingPerson === 'success') {
      setShowValidationAlert(!personValidateData.isValidPerson);
      if (
        !personValidateData.isValidPerson ||
        (personValidateData.personExist && !personValidateData.personInnova)
      ) {
        setErrors({
          error: true,
          errorText: 'Ingrese una identificación válida',
        });
      }
    }
  }, [requestingPerson]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (getInputProps('idNumber').value && !getInputProps('idNumber').error) {
        setIsLoading(true);
        const idNumber = getInputProps('idNumber').value as string;
        const idType = getInputProps('idType').value as string;
        validatePersonAction(idNumber, idType);
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [getInputProps('idNumber').value]);

  useEffect(() => {
    if (requestingPerson === 'success' && refetch) {
      setRefetch(false);
    }
  }, [requestingPerson]);

  function closeForCancel(): void {
    onCloseRequested(!editMode, currentValues);
    setErrors({ error: false, errorText: '' });
    setShowValidationAlert(false);
  }

  const naturalPersonData = useCallback(async () => {
    const person = await processRequest<
      NetworkResponse<NaturalPersonEquifaxResponse>
    >({
      method: 'GET',
      url: `/Person/personinformation?ruc=${getInputProps('idNumber').value}`,
    });

    await payerNaturalPersonEquifax({
      person,
      dataFields: currentValues,
      getInputProps,
      setNewPayerDataFieldsAction,
    });
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (
      requestingPerson === 'success' &&
      personValidateData.isValidPerson &&
      identificationType === 'N'
    ) {
      naturalPersonData();
    }
  }, [requestingPerson]);

  useEffect(() => {
    if (!personValidateData.isValidPerson || identificationType !== 'N') {
      setIsLoading(false);
    }
  }, [personValidateData]);

  return (
    <Modal
      data-testid="payment-issuer-modal"
      title="Responsable de pago"
      open={open}
      maxWidth="sm"
      onCloseRequested={closeForCancel}
    >
      <form data-testid="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <PersonValidationAlert
              data-testid="person-validation-alert"
              onCloseAlert={() => setShowValidationAlert(false)}
              showAlert={showValidationAlert}
            />
          </Grid>
          <Grid item xs={12}>
            <Grid container alignItems="center" justifyContent="center">
              <img alt="info" src={info} className={classes.callToActionIcon} />
              <Typography
                variant="caption"
                className={classes.callToActionCaption}
              >
                Necesitaremos la siguiente información de la persona que va a
                pagar la póliza.
              </Typography>
            </Grid>
          </Grid>

          {/* First card: type */}
          <Grid item xs={12}>
            <DefaultCard title="Tipo de identificación">
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <DropDown
                    inputName="idType"
                    inputLabel=""
                    defaultValue={getInputProps('idType').value as string}
                    options={idTypes}
                    onChange={(newVal): void => {
                      getInputProps('idType').onChange(newVal.Value);
                      changeIdType(newVal.Value);
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  {makeTextIdField({
                    name: 'idNumber',
                    label: 'Número de identificación',
                    getInputProps: () => ({
                      ...getInputProps('idNumber'),
                      error: errors.error || getInputProps('idNumber').error,
                      errorText:
                        errors.errorText || getInputProps('idNumber').errorText,
                      onChange(newValue) {
                        getInputProps('idNumber').onChange(newValue);
                        setRefetch(true);
                        setErrors({
                          error: getInputProps('idNumber').error,
                          errorText: getInputProps('idNumber').errorText,
                        });
                      },
                    }),
                    helperText:
                      (getInputProps('idType').value as string) === 'U'
                        ? 'Debe iniciar con 1 y contener 12 caracteres'
                        : 'Sin espacios ni guiones (ej. 888888888)',
                  })}
                </Grid>
              </Grid>
            </DefaultCard>
          </Grid>

          {/* Second card: data */}
          <Grid item xs={12}>
            {isLoading ? (
              <LoadingEquifax />
            ) : (
              <DefaultCard title="Datos del responsable de pago">
                <br />
                <Grid container spacing={2}>
                  {!isLegalPayer && (
                    <>
                      <Grid item xs={12} md={6}>
                        {makeTextField({
                          label: 'Nombres',
                          name: 'names',
                          getInputProps,
                        })}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        {makeTextField({
                          label: 'Apellidos',
                          getInputProps,
                          name: 'lastNames',
                        })}
                      </Grid>
                    </>
                  )}
                  {isLegalPayer && (
                    <>
                      <Grid item xs={12} md={12}>
                        {makeTextField({
                          label: 'Nombre de la razón social',
                          name: 'names',
                          getInputProps,
                        })}
                      </Grid>
                    </>
                  )}
                  {!isLegalPayer && (
                    <>
                      <Grid item xs={12} md={6}>
                        <TextField
                          inputName="birthday"
                          inputLabel="Fecha de Nacimiento (dd/mm/yyyy)"
                          placeholder="DD/MM/AAAA"
                          onChange={getInputProps('birthday').onChange}
                          inputError={getInputProps('birthday').error}
                          defaultValue={
                            getInputProps('birthday').value as string
                          }
                          inputErrorMessage={
                            getInputProps('birthday').errorText
                          }
                          maskOptions={{
                            date: true,
                            delimiter: '/',
                            datePattern: ['d', 'm', 'Y'],
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <RadioButtonGroup
                          radioGroupName="gender"
                          formTitle="Género"
                          radioData={genders}
                          gridSize={6}
                          defaultValue={getInputProps('gender').value as string}
                          onChange={(newVal): void => {
                            getInputProps('gender').onChange(newVal.Value);
                          }}
                          errorMessage={getInputProps('gender').errorText}
                        />
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12} md={6}>
                    {makeTextField({
                      getInputProps,
                      label: 'Correo Electrónico',
                      name: 'email',
                    })}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    {makeTextField({
                      name: 'phoneNumber',
                      getInputProps,
                      label: phoneText,
                    })}
                  </Grid>
                </Grid>
              </DefaultCard>
            )}
          </Grid>

          {/* Desktop submit button */}
          <Hidden smDown>
            <Grid item xs={12} container justifyContent="center">
              <DefaultButton
                data-testid="desktop-button"
                type="submit"
                forcedWidth={300}
                disabled={refetch || isLoading}
              >
                Guardar
              </DefaultButton>
            </Grid>
          </Hidden>

          {/* Mobile Submit Button */}
          <Hidden mdUp>
            <div className={classes.mobileButtonContainerOffset} />
            <div className={classes.mobileButtonContainer}>
              <DefaultButton
                data-testid="mobile-button"
                type="submit"
                fullWidth
              >
                Guardar
              </DefaultButton>
            </div>
          </Hidden>
        </Grid>
      </form>
    </Modal>
  );
};

interface Props {
  editMode: boolean;
  currentValues: PaymentIssuer;
  open: boolean;
  onCloseRequested: (mustResetValues: boolean, data: PaymentIssuer) => void;
  validatePersonAction: (
    idNumber: string,
    idType: string,
  ) => Promise<AnyAction>;
  setNewPayerDataFieldsAction: (dataFields: Partial<PaymentIssuer>) => void;
  personValidateData: PersonDataResponse;
}

export default PaymentIssuerModal;
